import {
  INCH_IN_CM,
  INCH_IN_FOOT,
  POUND_IN_STONE,
  convertLengthToImperialUnits,
  convertWeightToImperialUnits,
  feetAndInchesToCm,
  formatDecimal,
  inchesToCm,
  kgToPounds,
  poundsToKg,
  roundToDecimals,
  stonesAndPoundsToKg,
} from '@common/utils'

export type MeasurementKind = 'length' | 'mass'

export type UnitPart = {
  key: string
  suffixKey: string
  displayDecimals: number
}

export type UnitVariant = {
  id: string
  labelKey: string
  parts: UnitPart[]
  toCanonical: (parts: Record<string, number>) => number
  fromCanonical: (canonical: number) => Record<string, number>
}

export type MeasurementSpec = {
  kind: MeasurementKind
  storageDecimals: number
  defaultVariantId: string
  variants: UnitVariant[]
}

const lengthVariants: UnitVariant[] = [
  {
    id: 'cm',
    labelKey: 'cm',
    parts: [{key: 'cm', suffixKey: 'cm', displayDecimals: 1}],
    toCanonical: (parts) => parts.cm,
    fromCanonical: (cm) => ({cm}),
  },
  {
    id: 'ft_in',
    labelKey: 'ft / in',
    parts: [
      {key: 'ft', suffixKey: 'ft', displayDecimals: 0},
      {key: 'in', suffixKey: 'in', displayDecimals: 1},
    ],
    toCanonical: (parts) => feetAndInchesToCm(parts.ft, parts.in),
    fromCanonical: (cm) => {
      const {feet, inches} = convertLengthToImperialUnits(cm)
      // Keep remainder inches below 12 after display rounding (e.g. 11.96 in -> 1 ft).
      if (roundToDecimals(inches, 1) >= INCH_IN_FOOT) {
        return {ft: feet + 1, in: 0}
      }
      return {ft: feet, in: inches}
    },
  },
  {
    id: 'in',
    labelKey: 'in',
    parts: [{key: 'in', suffixKey: 'in', displayDecimals: 1}],
    toCanonical: (parts) => inchesToCm(parts.in),
    fromCanonical: (cm) => ({in: cm / INCH_IN_CM}),
  },
]

const massVariants: UnitVariant[] = [
  {
    id: 'kg',
    labelKey: 'kg',
    parts: [{key: 'kg', suffixKey: 'kg', displayDecimals: 1}],
    toCanonical: (parts) => parts.kg,
    fromCanonical: (kg) => ({kg}),
  },
  {
    id: 'lb',
    labelKey: 'lb',
    parts: [{key: 'lb', suffixKey: 'lb', displayDecimals: 1}],
    toCanonical: (parts) => poundsToKg(parts.lb),
    fromCanonical: (kg) => ({lb: kgToPounds(kg)}),
  },
  {
    id: 'st_lb',
    labelKey: 'st / lb',
    parts: [
      {key: 'st', suffixKey: 'st', displayDecimals: 0},
      {key: 'lb', suffixKey: 'lb', displayDecimals: 1},
    ],
    toCanonical: (parts) => stonesAndPoundsToKg(parts.st, parts.lb),
    fromCanonical: (kg) => {
      const {stones, pounds} = convertWeightToImperialUnits(kg)
      // Keep remainder pounds below 14 after display rounding.
      if (roundToDecimals(pounds, 1) >= POUND_IN_STONE) {
        return {st: stones + 1, lb: 0}
      }
      return {st: stones, lb: pounds}
    },
  },
]

export const MEASUREMENT_SPECS: Record<MeasurementKind, MeasurementSpec> = {
  length: {
    kind: 'length',
    storageDecimals: 1,
    defaultVariantId: 'cm',
    variants: lengthVariants,
  },
  mass: {
    kind: 'mass',
    storageDecimals: 1,
    defaultVariantId: 'kg',
    variants: massVariants,
  },
}

export const parseCanonical = (raw: string | undefined): number | undefined => {
  if (raw == null || raw.trim() === '') {
    return undefined
  }
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : undefined
}

const parsePartNumber = (raw: string): number | undefined => {
  const trimmed = raw.trim()
  if (trimmed === '') {
    return undefined
  }
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : undefined
}

export const partsToCanonical = (
  variant: UnitVariant,
  partStrings: Record<string, string>,
): number | undefined => {
  const numericParts: Record<string, number> = {}
  let anyFilled = false
  let hasInvalid = false

  variant.parts.forEach((part) => {
    const raw = (partStrings[part.key] ?? '').trim()
    // Blank compound parts default to 0 (e.g. 5 ft with empty inches).
    // Non-empty garbage ('.', '-', 'e') invalidates the whole measurement.
    if (raw === '') {
      numericParts[part.key] = 0
      return
    }

    const parsed = parsePartNumber(raw)
    if (parsed == null) {
      hasInvalid = true
      return
    }

    anyFilled = true
    numericParts[part.key] = parsed
  })

  if (hasInvalid || !anyFilled) {
    return undefined
  }

  return variant.toCanonical(numericParts)
}

export const canonicalToPartStrings = (
  variant: UnitVariant,
  canonical: number | undefined,
): Record<string, string> => {
  if (canonical == null || !Number.isFinite(canonical)) {
    return Object.fromEntries(variant.parts.map((part) => [part.key, '']))
  }

  const numbers = variant.fromCanonical(canonical)
  return Object.fromEntries(
    variant.parts.map((part) => [
      part.key,
      formatDecimal(numbers[part.key] ?? 0, part.displayDecimals),
    ]),
  )
}

export const formatStoredCanonical = (
  canonical: number,
  storageDecimals: number,
): string => formatDecimal(canonical, storageDecimals)
